import find from 'latest_modules/lodash/find';
import includes from 'latest_modules/lodash/includes';
import merge from 'latest_modules/lodash/merge';
import Vue from 'vue';
import Payment1 from '../schema/Payment';

const { Payment, Card } = Payment1;

import { SERVICES } from '../../constants/service';

import paymentsApi from '../../api/payments';
import { PAYMENTS as paymentsTypes } from '../mutation-types';

const { __ } = require('../../util/i18n').default;

// State
const state = merge(Payment.state, {
  service: SERVICES.SHOP,
  cards: {},
  // TODO: shippings/payments 分離前のマッピングデータ
  shippingMap: {}
});

// Getters
const getters = merge(Payment.getters, {
  findCardById(state) {
    return (id) => state.cards[id];
  },

  current(state) {
    return find(state.all, (s) => includes(s.is_default_for, state.service));
  },

  // 決済情報別の画像パスを取得
  // @param {Object} payment Payment オブジェクト
  getPaymentMethodImagePath(state) {
    return function(payment) {
      let src = '';
      let alt = payment.method;
      switch (payment.method) {
        case Payment.PAYMENT_METHOD.STRIPE:
          var card = state.cards[payment.stripe.card_id];
          switch (card.brand) {
            case Card.CARD_BRANDS.VISA:
              src = 'brands/visa.png';
              break;
            case Card.CARD_BRANDS.AMEX:
              src = 'brands/amex.png';
              break;
            case Card.CARD_BRANDS.MASTER:
              src = 'brands/master.png';
              break;
            case Card.CARD_BRANDS.DISCOVER:
              src = 'brands/discover.png';
              break;
            case Card.CARD_BRANDS.JCB:
              src = 'brands/jcb.png';
              break;
            case Card.CARD_BRANDS.DINERS:
              src = 'brands/diners.png';
              break;
            default:
              src = 'credit_card.png';
              break;
          }
          alt = card.brand;
          break;
        case Payment.PAYMENT_METHOD.PAYPAL:
          src = 'card_paypal.png';
          break;
        case Payment.PAYMENT_METHOD.AMAZON:
          src = 'card_amazon.png';
          break;
      }

      return {
        src: `/static/common/icons/${src}`,
        alt
      };
    };
  },

  // 決済情報別の名称を取得
  // @param {Object} payment Payment オブジェクト
  getPaymentMethodLabel(state) {
    return function(payment) {
      switch (payment.method) {
        case Payment.PAYMENT_METHOD.STRIPE:
          var card = state.cards[payment.stripe.card_id];
          return `${__('Card Ending in')} ${card.last4}`;
        case Payment.PAYMENT_METHOD.PAYPAL:
          return 'PayPal';
        case Payment.PAYMENT_METHOD.AMAZON:
          return 'Amazon Pay';
      }
    };
  },

  // TODO: shippings/payments 分離前の payment 取得
  getPaymentByShippingId(state, getters) {
    return (id) => getters.findById(state.shippingMap[id]);
  }
});

// Actions
const actions = {
  /*
  使用するサービスを選択する
  */
  setService({
    commit
  }, service) {
    commit(paymentsTypes.SERVICE_CHANGE, service);
    return Promise.resolve(service);
  },

  /*
  登録されている決済情報を取得する
  */
  fetchAll({
    commit
  }) {
    return new Promise(function(resolve, reject) {
      paymentsApi.getAll(function(err, res) {
        if (err) {
          return reject(err);
        }
        // 一度データを空にする
        commit(paymentsTypes.CLEAR);
        for (let payment of res.payments) {
          commit(paymentsTypes.ADD, payment);
        }
        resolve();
      });
    });
  },

  /*
  現在デフォルトに指定されている決済情報を取得する
  */
  fetchCurrent({
    commit,
    state,
    getters
  }) {
    return new Promise(function(resolve, reject) {
      paymentsApi.getCurrent({
        service: state.service
      }, function(err, res) {
        if (err) {
          return reject(err);
        }
        if (res.payment && res.payment.payment) {
          // すでにデータが存在すれば先に削除する
          if (getters.findById(res.payment.payment._id)) {
            commit(paymentsTypes.REMOVE, {
              _id: res.payment.payment._id
            });
          }
          commit(paymentsTypes.ADD, res.payment);
        }
        resolve();
      });
    });
  },

  /*
  決済情報を新規追加する
  @param {String} payload.method 決済方法
  @param {String} payload.shipping_id Shipping ID
  @param {String} payload.token (※決済方法がStripeの場合のみ) Stripe token
  */
  add({
    commit
  }, payload) {
    return new Promise(function(resolve, reject) {
      const params = {
        method: payload.method,
        shipping_id: payload.shipping_id
      };
      if (
        [Payment.PAYMENT_METHOD.STRIPE, Payment.PAYMENT_METHOD.MOBILE].includes(
          params.method
        )
      ) {
        params.token = payload.token;

        if (params.method === Payment.PAYMENT_METHOD.STRIPE) {
          params['g-recaptcha-response'] = payload.recaptcha_token;
          params.payment_method_id = payload.payment_method_id;
        }
      }

      paymentsApi.post(params, function(err, res) {
        if (err) {
          return reject(err);
        }
        for (let payment of res.payments) {
          commit(paymentsTypes.ADD, payment);
        }
        resolve(res);
      });
    });
  },

  /*
  決済情報を削除する
  @param {String} payload._id 決済方法
  @param {String} payload.shipping_id Shipping ID
  @param {String} payload.token (※決済方法がStripeの場合のみ) Stripe token
  */
  remove({
    commit
  }, payload) {
    return new Promise(function(resolve, reject) {
      const params = {
        id: payload._id,
        shipping_id: payload.shipping_id
      };

      paymentsApi.delete(params, function(err) {
        if (err) {
          return reject(err);
        }
        commit(paymentsTypes.REMOVE, {
          _id: payload._id
        });
        resolve();
      });
    });
  },

  /*
  使用する決済情報を変更する (defaultFor を設定する)
  @param {String} payload._id Payment id
  */
  changeCurrent({
    commit,
    state
  }, payload) {
    return new Promise(function(resolve, reject) {
      const params = {
        id: payload._id,
        service: state.service
      };

      if (!params.id) {
        return reject(new Error('No payment method specified.'));
      }

      paymentsApi.postCurrent(params, function(err) {
        if (err) {
          return reject(err);
        }
        commit(paymentsTypes.CURRENT_CHANGE, {
          _id: params.id
        });
        resolve(state.all[params.id]);
      });
    });
  },

  /*
  Credit cardにBilling Addressを登録する
  */
  updateBillingAddress({
    commit
  }, payload) {
    return new Promise(function(resolve, reject) {
      const params = {
        address_line1: payload.address_line1,
        address_line2: payload.address_line2,
        address_city: payload.address_city,
        address_state: payload.address_state,
        address_zip: payload.address_zip,
        address_country: payload.address_country,
        payment_id: payload.payment_id
      };

      paymentsApi.updateBillingAddress(params, function(err, res) {
        if (err) {
          return reject(err);
        }
        commit(paymentsTypes.UPDATE_CARD, {
          id: res.card.id,
          card: res.card
        });
        resolve(res);
      });
    });
  },

  /*
  Credit Cardの情報（Expiry と billing address）をupdateする
  */
  updateCard({
    commit
  }, payload) {
    return new Promise(function(resolve, reject) {
      const params = {
        payment_id: payload.payment_id,
        method: Payment.PAYMENT_METHOD.STRIPE, // stripe only
        card: {
          address_line1: payload.address_line1,
          address_line2: payload.address_line2,
          address_city: payload.address_city,
          address_state: payload.address_state,
          address_zip: payload.address_zip,
          address_country: payload.address_country,
          exp_month: payload.exp_month,
          exp_year: payload.exp_year
        }
      };

      paymentsApi.updateCard(params, function(err, res) {
        if (err) {
          return reject(err);
        }
        commit(paymentsTypes.UPDATE_CARD, {
          id: res.card.id,
          card: res.card
        });
        resolve(res);
      });
    });
  }
};

// Mutations
const mutations = {
  [paymentsTypes.SERVICE_CHANGE](state, service) {
    state.service = service;
  },

  [paymentsTypes.CLEAR](state) {
    Vue.set(state, 'all', {});
    Vue.set(state, 'allList', []);
  },

  [paymentsTypes.ADD](state, payload) {
    const payment = new Payment(payload.payment).toObject();
    Vue.set(state.all, payload.payment._id, payment);
    state.allList.push(payment._id);
    if (payload.card) {
      Vue.set(state.cards, payload.card.id, new Card(payload.card).toObject());
    }

    // TODO: shippings/payments 分離前のマッピングデータ
    const shippings =
      payload.payment[payload.payment.method] &&
      payload.payment[payload.payment.method].shippings;
    if (shippings) {
      shippings.forEach(
        (shippingId) => (state.shippingMap[shippingId] = payload.payment._id)
      );
    }
  },

  [paymentsTypes.REMOVE](state, payload) {
    const payment = state.all[payload._id];
    if (payment.method === Payment.PAYMENT_METHOD.STRIPE) {
      Vue.delete(state.cards, payment.stripe.card_id);
    }

    Vue.delete(state.allList, state.allList.indexOf(payload._id));
    Vue.delete(state.all, payload._id);
  },

  [paymentsTypes.CURRENT_CHANGE](state, payload) {
    return state.allList.map(function(id) {
      const payment = state.all[id];
      if (includes(payment.is_default_for, state.service)) {
        // _.pull を使用すると current のリアクティブが検出されない
        Vue.delete(
          payment.is_default_for,
          payment.is_default_for.indexOf(state.service)
        );
      }
      if (payment._id === payload._id) {
        payment.is_default_for.push(state.service);
      }
    });
  },

  [paymentsTypes.UPDATE_CARD](state, payload) {
    const {
      id,
      card
    } = payload;
    state.cards[id] = merge(state.cards[id], card);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
